import PropTypes from "prop-types";
import { createContext, useEffect, useReducer } from "react";

// third party
import { Chance } from "chance";
import { jwtDecode } from "jwt-decode";

// reducer - state management
import { LOGIN, LOGOUT } from "store/actions";
import accountReducer from "store/accountReducer";

// project imports
import Loader from "ui-component/Loader";
import axios from "utils/axios";

// Get API base URL from environment
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "/api";

const chance = new Chance();

// constant
const initialState = {
  isLoggedIn: false,
  isInitialized: false,
  user: null,
};

function verifyToken(serviceToken) {
  if (!serviceToken) {
    return false;
  }

  const decoded = jwtDecode(serviceToken);

  // Ensure 'exp' exists and compare it to the current timestamp
  if (!decoded.exp) {
    throw new Error("Token does not contain 'exp' property.");
  }

  return decoded.exp > Date.now() / 1000;
}

function setSession(serviceToken, storageType = "localStorage") {
  if (serviceToken) {
    if (storageType === "sessionStorage") {
      sessionStorage.setItem("serviceToken", serviceToken);
      localStorage.removeItem("serviceToken");
    } else {
      localStorage.setItem("serviceToken", serviceToken);
      sessionStorage.removeItem("serviceToken");
    }
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
  } else {
    localStorage.removeItem("serviceToken");
    sessionStorage.removeItem("serviceToken");
    delete axios.defaults.headers.common.Authorization;
  }
}

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //

const JWTContext = createContext(null);

export function JWTProvider({ children }) {
  const [state, dispatch] = useReducer(accountReducer, initialState);

  useEffect(() => {
    const init = async () => {
      try {
        const localToken = window.localStorage.getItem("serviceToken");
        const sessionToken = window.sessionStorage.getItem("serviceToken");
        const serviceToken = localToken || sessionToken;

        if (serviceToken && verifyToken(serviceToken)) {
          const tokenStorage = localToken ? "localStorage" : "sessionStorage";
          setSession(serviceToken, tokenStorage);
          const response = await axios.get("/me");
          const { user } = response.data;
          dispatch({
            type: LOGIN,
            payload: {
              isLoggedIn: true,
              user,
            },
          });
        } else {
          dispatch({
            type: LOGOUT,
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: LOGOUT,
        });
      } finally {
        // Mark as initialized after checking authentication
        dispatch({
          type: "INITIALIZE",
          payload: { isInitialized: true },
        });
      }
    };

    init();
  }, []);

  const login = async (email, password, rememberMe = true) => {
    try {
      const response = await axios.post("/login", { email, password });
      const { serviceToken, user } = response.data;
      const storageType = rememberMe ? "localStorage" : "sessionStorage";
      setSession(serviceToken, storageType);
      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: true,
          user,
        },
      });
    } catch (error) {
      // Extract error message from response
      // The error might be in different places depending on axios configuration
      const errorMessage =
        error.response?.data?.error ||
        error.data?.error ||
        error.error ||
        error.message ||
        "Login failed";
      throw new Error(errorMessage);
    }
  };

  const register = async (email, password, firstName, lastName) => {
    // todo: this flow need to be recode as it not verified
    const id = chance.bb_pin();
    const response = await axios.post("/api/account/register", {
      id,
      email,
      password,
      firstName,
      lastName,
    });
    let users = response.data;

    if (
      window.localStorage.getItem("users") !== undefined &&
      window.localStorage.getItem("users") !== null
    ) {
      const localUsers = window.localStorage.getItem("users");
      users = [
        ...JSON.parse(localUsers),
        {
          id,
          email,
          password,
          name: `${firstName} ${lastName}`,
        },
      ];
    }

    window.localStorage.setItem("users", JSON.stringify(users));
  };

  const logout = () => {
    setSession(null);
    dispatch({ type: LOGOUT });
  };

  const forgotPassword = async (email) => {
    try {
      const response = await axios.post("/forgot-password", { email });
      return response.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        error.data?.error ||
        error.error ||
        error.message ||
        "Failed to send reset email";
      throw new Error(errorMessage);
    }
  };

  const verifyResetToken = async (token) => {
    try {
      const response = await axios.get(`/verify-reset-token?token=${token}`);
      return response.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        error.data?.error ||
        error.error ||
        error.message ||
        "Invalid token";
      throw new Error(errorMessage);
    }
  };

  const resetPassword = async (token, newPassword) => {
    try {
      const response = await axios.post("/reset-password", {
        token,
        new_password: newPassword,
      });
      return response.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        error.data?.error ||
        error.error ||
        error.message ||
        "Failed to reset password";
      throw new Error(errorMessage);
    }
  };

  const updateProfile = () => {};

  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loader />;
  }

  return (
    <JWTContext
      value={{
        ...state,
        login,
        logout,
        register,
        forgotPassword,
        verifyResetToken,
        resetPassword,
        updateProfile,
      }}
    >
      {children}
    </JWTContext>
  );
}

export default JWTContext;

JWTProvider.propTypes = { children: PropTypes.node };
